import { useMemo } from "react";
import { Card, Space, Typography } from "antd";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";

const { Text } = Typography;

interface BackgroundLog {
  _id: string;
  job_type: string;
  job_id: string;
  timestamp: string;
  message: string;
  stack_trace: string | null;
  metadata: {
    appkey: string;
    celery_task_id: string;
    source_uri: string;
  };
}

interface JobStatsChartsProps {
  logs: BackgroundLog[];
}

export default function JobStatsCharts({ logs }: JobStatsChartsProps) {
  const jobTypeStats = useMemo(() => {
    const stats: Record<string, { total: number; success: number; failed: number }> = {};

    logs.forEach((log) => {
      if (!stats[log.job_type]) {
        stats[log.job_type] = { total: 0, success: 0, failed: 0 };
      }

      stats[log.job_type].total += 1;

      const isFailed =
        log.stack_trace !== null ||
        log.message.toLowerCase().includes("error") ||
        log.message.toLowerCase().includes("fail");

      if (isFailed) {
        stats[log.job_type].failed += 1;
      } else {
        stats[log.job_type].success += 1;
      }
    });

    return Object.entries(stats).map(([type, data]) => ({
      name: type,
      ...data,
    }));
  }, [logs]);

  const timelineStats = useMemo(() => {
    const stats: Record<string, { success: number; failed: number }> = {};

    logs.forEach((log) => {
      const date = dayjs(log.timestamp).format("MMM DD");

      if (!stats[date]) {
        stats[date] = { success: 0, failed: 0 };
      }

      const isFailed =
        log.stack_trace !== null ||
        log.message.toLowerCase().includes("error") ||
        log.message.toLowerCase().includes("fail");

      if (isFailed) {
        stats[date].failed += 1;
      } else {
        stats[date].success += 1;
      }
    });

    return Object.entries(stats).map(([date, data]) => ({
      date,
      ...data,
    }));
  }, [logs]);

  const statusPie = useMemo(() => {
    const success = logs.filter((log) => !log.stack_trace && log.message.toLowerCase().includes("success")).length;
    const failed = logs.filter(
      (log) =>
        log.stack_trace !== null ||
        log.message.toLowerCase().includes("error") ||
        log.message.toLowerCase().includes("fail"),
    ).length;

    return [
      { name: "Success", value: success, fill: "#10b981" },
      { name: "Failed", value: failed, fill: "#ef4444" },
    ];
  }, [logs]);

  const COLORS = ["#10b981", "#ef4444"];

  return (
    <Space direction="vertical" size="large" className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-xs text-gray-500 block mb-1">Total Jobs</Text>
              <Text className="text-2xl font-bold text-gray-900">{logs.length}</Text>
            </div>
          </div>
        </Card>

        <Card className="shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-xs text-gray-500 block mb-1">Success Rate</Text>
              <Text className="text-2xl font-bold text-green-600">
                {logs.length > 0
                  ? (
                      (logs.filter((log) => !log.stack_trace && log.message.toLowerCase().includes("success")).length /
                        logs.length) *
                      100
                    ).toFixed(1)
                  : 0}
                %
              </Text>
            </div>
          </div>
        </Card>

        <Card className="shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-xs text-gray-500 block mb-1">Failed Jobs</Text>
              <Text className="text-2xl font-bold text-red-600">
                {
                  logs.filter(
                    (log) =>
                      log.stack_trace !== null ||
                      log.message.toLowerCase().includes("error") ||
                      log.message.toLowerCase().includes("fail"),
                  ).length
                }
              </Text>
            </div>
          </div>
        </Card>
      </div>

      <Card title="Jobs by Status" className="shadow-sm">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={statusPie}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, value }) => `${name}: ${value}`}
              outerRadius={80}
              dataKey="value"
            >
              {statusPie.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Jobs by Type" className="shadow-sm">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={jobTypeStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="success" fill="#10b981" name="Success" />
            <Bar dataKey="failed" fill="#ef4444" name="Failed" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Jobs Over Time" className="shadow-sm">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={timelineStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="success" stroke="#10b981" name="Success" strokeWidth={2} />
            <Line type="monotone" dataKey="failed" stroke="#ef4444" name="Failed" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </Space>
  );
}
